import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, slicePage, totalPagesFromCount } from "../lib/adminPagination";
import { isManilaPastDate } from "../lib/dateTime";
import { supabase } from "../supabaseClient";

const PAGE_SIZE = ADMIN_PAGE_SIZE;

const PROJECT_ELIGIBLE_STATUSES = ["accepted", "approved", "event_preparation", "event_live"];

const projectStages = [
  { id: "event_preparation", label: "Event Preparation" },
  { id: "event_live", label: "Posted on Live Event" },
];

const filterableStages = projectStages;

const stageIndexById = projectStages.reduce((accumulator, stage, index) => {
  accumulator[stage.id] = index;
  return accumulator;
}, {});

const stageLabelById = projectStages.reduce((accumulator, stage) => {
  accumulator[stage.id] = stage.label;
  return accumulator;
}, {});

const normalizeStage = (value) => {
  const normalized = String(value || "").trim().toLowerCase().replace(/\s+/g, "_");
  return stageIndexById[normalized] !== undefined ? normalized : "event_preparation";
};

const resolveProjectStage = (row) => {
  const candidates = [row?.project_stage, row?.booking_status];
  for (const candidate of candidates) {
    const normalized = String(candidate || "").trim().toLowerCase().replace(/\s+/g, "_");
    if (stageIndexById[normalized] !== undefined) {
      return normalized;
    }
  }
  return "event_preparation";
};

const statusLabel = (value) => {
  const normalized = String(value || "approved").trim().toLowerCase().replace(/\s+/g, "_");
  if (normalized === "in_review") return "In Review";
  if (normalized === "initial_consultation") return "Initial Consultation";
  if (normalized === "proposal_contract" || normalized === "proposal_quotation" || normalized === "agreement_confirmation" || normalized === "contract_signing") {
    return "Proposal & Contract";
  }
  if (normalized === "event_preparation" || normalized === "accepted" || normalized === "approved") return "Event Preparation";
  if (normalized === "event_live") return "Posted on Live Event";
  if (normalized === "declined") return "Declined";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).replace(/_/g, " ");
};

const formatDisplayDate = (value) => {
  if (!value) return "TBD";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "TBD";
  return parsed.toLocaleDateString("en-PH", { timeZone: "Asia/Manila", month: "short", day: "numeric", year: "numeric" });
};

const Projects = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState("concert");
  const [stageFilter, setStageFilter] = useState("all");

  const fetchProjects = async () => {
    try {
      const [{ data: bookingRows, error: bookingError }, { data: eventRows, error: eventError }] = await Promise.all([
        supabase
          .from("bookings")
          .select("*")
          .in("status", PROJECT_ELIGIBLE_STATUSES)
          .order("updated_at", { ascending: false })
          .range(0, 199),
        supabase
          .from("events")
          .select("booking_request_code, status")
          .in("status", ["published", "upcoming"]),
      ]);

      if (bookingError || eventError) throw bookingError || eventError;

      const liveRequestCodes = new Set(
        (eventRows || [])
          .filter((eventRow) => String(eventRow?.booking_request_code || "").trim())
          .map((eventRow) => String(eventRow.booking_request_code).trim())
      );

      setProjects(
        (bookingRows || []).map((booking) => {
          const requestCode = String(booking?.request_code || "").trim();
          const inferredStage = liveRequestCodes.has(requestCode)
            ? "event_live"
            : normalizeStage(resolveProjectStage(booking));

          return {
            ...booking,
            project_stage: inferredStage,
          };
        })
      );
    } catch (error) {
      console.error("Failed to load projects:", error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();

    const channel = supabase
      .channel("admin-projects-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, fetchProjects)
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, fetchProjects)
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const filteredProjects = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return projects.filter((project) => {
      const isPast = isManilaPastDate(project.event_date);
      const projectType = project.booking_type === "corporate" ? "corporate" : "concert";
      const matchTab =
        activeTab === "past"
          ? isPast
          : !isPast && projectType === activeTab;
      const matchStage = stageFilter === "all" || project.project_stage === stageFilter;
      if (!matchTab || !matchStage) return false;

      if (!needle) return true;
      const matchSearch = [
        project.requester_name,
        project.requester_email,
        project.request_code,
        project.event_name,
        project.theme,
        project.venue,
        statusLabel(project.status),
        projectStages.find((stage) => stage.id === project.project_stage)?.label,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));

      return matchSearch;
    });
  }, [projects, search, activeTab, stageFilter]);

  const tabCounts = useMemo(() => {
    const counts = {
      concert: 0,
      corporate: 0,
      past: 0,
    };

    projects.forEach((project) => {
      if (isManilaPastDate(project.event_date)) {
        counts.past += 1;
        return;
      }
      if (project.booking_type === "corporate") {
        counts.corporate += 1;
      } else {
        counts.concert += 1;
      }
    });

    return counts;
  }, [projects]);

  const totalPages = totalPagesFromCount(filteredProjects.length, PAGE_SIZE);
  const paginatedProjects = slicePage(filteredProjects, currentPage, PAGE_SIZE);
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  return (
    <div className="min-h-screen bg-[#f3f3f3]">
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto space-y-6 min-w-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl md:text-[26px] font-black text-gray-900 tracking-tight uppercase italic">Projects</h1>
              <p className="mt-1 text-xs text-gray-500">Accepted bookings appear here and move through the project stages.</p>
            </div>

            <div className="relative w-full sm:w-auto sm:max-w-xs sm:min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search accepted projects..."
                className="w-full h-9 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-xs text-gray-700"
              />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-6 space-y-4" ref={listTopRef}>
            <div className="flex flex-wrap gap-4 sm:gap-6 border-b border-gray-200 mb-4">
              {[
                { key: "concert", label: `Concerts (${tabCounts.concert})` },
                { key: "corporate", label: `Corporate Events (${tabCounts.corporate})` },
                { key: "past", label: `Past (${tabCounts.past})` },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setStageFilter("all");
                    setCurrentPage(1);
                  }}
                  className={`pb-2 text-sm ${
                    activeTab === tab.key
                      ? "text-gray-900 border-b-2 border-red-600"
                      : "text-gray-500"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <BusyTable show={showSkeleton} tone="admin">
            <div className="rounded-lg border border-gray-200 overflow-x-auto custom-scrollbar">
              <table className="w-full table-fixed border-collapse min-w-[860px]">
                <thead>
                  <tr className="bg-black text-white text-sm">
                    <th className="px-4 py-3 text-left w-1/4">Client Name</th>
                    <th className="px-4 py-3 text-left w-1/5">Event Name</th>
                    <th className="px-4 py-3 text-left w-1/6">Date</th>
                    <th className="px-4 py-3 text-left w-1/5">
                      <div className="inline-flex items-center gap-2">
                        <span>Stage</span>
                        <select
                          value={stageFilter}
                          onChange={(event) => {
                            setStageFilter(event.target.value);
                            setCurrentPage(1);
                          }}
                          className="h-6 w-24 rounded border border-gray-500 bg-black px-1 text-[11px] text-white focus:outline-none"
                        >
                          <option value="all">All</option>
                          {filterableStages.map((stage) => (
                            <option key={stage.id} value={stage.id}>{stage.label}</option>
                          ))}
                        </select>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left w-[120px]">Status</th>
                    <th className="px-4 py-3 text-center w-[140px]">View</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProjects.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">
                        {loading ? "Loading..." : (search || stageFilter !== "all" ? "No projects match this search." : "No projects yet.")}
                      </td>
                    </tr>
                  ) : (
                    paginatedProjects.map((project) => {
                      const currentStage = stageLabelById[project.project_stage] || stageLabelById.initial_consultation;
                      return (
                        <tr key={project.id} className="border-t border-gray-100 text-sm">
                          <td className="px-4 py-4 text-gray-900">
                            <div className="font-medium">{project.requester_name || "Unknown"}</div>
                            <div className="text-xs font-normal text-gray-500">{project.request_code || `Project #${project.id}`}</div>
                          </td>
                          <td className="px-4 py-4 text-gray-600">{project.event_name || "Untitled Event"}</td>
                          <td className="px-4 py-4 text-gray-600">{formatDisplayDate(project.event_date || project.created_at)}</td>
                          <td className="px-4 py-4 text-gray-600">{currentStage}</td>
                          <td className="px-4 py-4">
                            <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                              {statusLabel(project.status)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/projects/${project.id}`)}
                              className="inline-flex items-center justify-center rounded-lg bg-[#8b0000] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#6f0000]"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            </BusyTable>

            <AdminPagination
              className="pt-2"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;